import {UserApi} from "../../../services/api/UserApi"
import React, {useContext, useEffect, useState} from "react"
import {useNavigate} from "react-router"
import {LoginForm} from "./form/LoginForm"
import {RegisterForm} from "./form/RegisterForm"
import {useTranslation} from "react-i18next";
import {capitalize} from "../../../utils";
import {EnterLogin} from "./form/EnterLogin";
import {AppContext} from "../../authProvider/AuthProvider";
import {LoginCarousel} from "./LoginCarousel";
import {ButtonType, UiButton} from "../../ui-components/button/uiButton";
import {LOGIN_MAX_LENGTH, LOGIN_MIN_LENGTH, PASSWORD_MIN_LENGTH} from "../../../constants/CONST";

export type LoginErrors = {
    loginIsOnError: boolean
    loginErrorMessage: string
    mailIsOnError: boolean
    mailErrorMessage: string
    passwordIsOnError: boolean
    passwordErrorMessage: string
    countryIsOnError: boolean
    countryErrorMessage: string
    globalErrorMessage: string
}

const defaultErrors: LoginErrors = {
    loginIsOnError: false,
    loginErrorMessage: "",
    mailIsOnError: false,
    mailErrorMessage: "",
    passwordIsOnError: false,
    passwordErrorMessage: "",
    countryIsOnError: false,
    countryErrorMessage: "",
    globalErrorMessage: "",
}

export const Login: React.FC = () => {
    const { t } = useTranslation()
    
    const navigate = useNavigate()

    const [isLoginPage, setIsLoginPage] = useState(true)
    const [loginIsFilled, setLoginIsFilled] = useState(false)
    const [title, setTitle] = useState(t("global.welcome-on-fastype"))
    const [isCarouselHidden, setisCarouselHidden] = useState(false)
    
    const [login, setLogin] = useState("")
    const [password, setPassword] = useState("")
    const [mail, setMail] = useState("")
    const [country, setCountry] = useState("")
    
    const [continueButtonText, setContinueButtonText] = useState(capitalize(t("global.continue")))
    
    const [errors, setErrors] = useState<LoginErrors>(defaultErrors)
    
    const {isConnected, setIsConnected, setUser} = useContext(AppContext)
    
    useEffect(() => {
        if (isConnected) {
            navigate("/");
        }
    }, []);
    
    function backToEnterLogin() {
        setContinueButtonText(capitalize(t("global.continue")));
        setLoginIsFilled(false);
        setIsLoginPage(true);
        setPassword("");
        setLogin("");
        
        setErrors(defaultErrors);
        setTitle(t("global.welcome-on-fastype"));
    }
    
    function checkForm(): boolean {
        let formIsValid = true;
        
        if (!isLoginPage) { // only for register

            if (!validateEmail(mail)) {
                formIsValid = false;

                setErrors(prevState => ({
                    ...prevState,
                    mailIsOnError: true,
                    mailErrorMessage: t("form.type-valid-email"),
                }));
            }

            if (password.length < PASSWORD_MIN_LENGTH) {
                formIsValid = false;
                setErrors(prevState => ({
                    ...prevState,
                    passwordIsOnError: true,
                    passwordErrorMessage: t("account.password-least-x-characters", {number: PASSWORD_MIN_LENGTH}),
                }));
            }
        }
        
        
        let emptyField = {};

        // login validation
        if (!loginIsFilled) {
            if (login.length < LOGIN_MIN_LENGTH) {
                formIsValid = false;
                setErrors(prevState => ({
                    ...prevState,
                    loginIsOnError: true,
                    loginErrorMessage: t("account.username-min-characters", {number: LOGIN_MIN_LENGTH}),
                }))
            }

            if (login.length > LOGIN_MAX_LENGTH) {
                formIsValid = false;
                setErrors(prevState => ({
                    ...prevState,
                    loginIsOnError: true,
                    loginErrorMessage: t("account.username-max-characters", {number: LOGIN_MAX_LENGTH}),
                }))
            }

            if (login === "") {
                emptyField = {
                    ...emptyField,
                    loginIsOnError: true,
                    loginErrorMessage: t("form.fill-login"),
                }
            }
        }
        
        if (loginIsFilled) {
            if (mail === "" && !isLoginPage) {
                emptyField = {
                    ...emptyField,
                    mailIsOnError: true,
                    mailErrorMessage: t("form.fill-email"),
                }
            }

            if (country === "" && !isLoginPage) {
                emptyField = {
                    ...emptyField,
                    countryIsOnError: true,
                    countryErrorMessage: t("form.fill-country"),
                }
            }

            if (password === "") {
                emptyField = {
                    ...emptyField,
                    passwordIsOnError: true,
                    passwordErrorMessage: t("form.fill-password"),
                }
            }
        }
        
        if (Object.keys(emptyField).length > 0) {
            setErrors(prevState => ({
                ...prevState,
                ...emptyField,
            }))
            formIsValid = false;
        }
        
        return formIsValid
    }

    function validateEmail(email: string) {
        return String(email)
          .toLowerCase()
          .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
    }

    async function loginToApi() {

        const [logSuccess, errorMessage] = await UserApi.login(login, password)

        if (!logSuccess) {
            if (errorMessage === "incorrect-password") {
                setErrors(prevState => ({
                    ...prevState,
                    passwordIsOnError: true,
                    passwordErrorMessage: t("form.incorrect-password"),
                }))
            } else {
                setErrors(prevState => ({
                    ...prevState,
                    globalErrorMessage: t("error.error-occured"),
                }))
            }
            
        } else {
            await getConnectedUser()
        }
    }
    
    async function getConnectedUser() {
        const user = await UserApi.getConnectedUser()

        if (user === null) {
            setErrors(prevState => ({
                ...prevState,
                globalErrorMessage: t("error.error-occured"),
            }))
            return
        }
        
        setUser(user)
        setIsConnected(true)
        navigate("/")
    }

    async function registerToApi() {

        const [accountIsCreated, api_message] = await UserApi.register(login, mail, password, country);

        if (accountIsCreated) {
            await getConnectedUser();        
        } else if (api_message?.field === "email") {            
            setErrors(prevState => ({
                ...prevState,
                mailIsOnError: true,
                mailErrorMessage: t(api_message.message),
            }))
        } else {
            const errorMessage = api_message !== null ? api_message.message: t("error.error-occured") 

            setErrors(prevState => ({
                ...prevState,
                globalErrorMessage: t(errorMessage),
            }))
        }
    }
    
    async function continueButton() {
        if (!checkForm()) {
            return
        }
        
        if (!loginIsFilled) {
            const userExist = await UserApi.userExist(login) as boolean
            
            setIsLoginPage(userExist)
            setLoginIsFilled(true)
            
            if (userExist) {
                setTitle(t("form.login-as", { name: login }))
                setContinueButtonText(capitalize(t("form.connect")))
            } else {
                setContinueButtonText(capitalize(t("form.create-account")))
            }
        } else if (!isLoginPage) { // register page
            await registerToApi()
        } else { // login page
            await loginToApi()
        }
    }

    return (
        <div className="flex h-screen items-center flex-col w-full">
            
            <div className="flex justify-evenly w-full h-full items-center">
                
                {/* login */}
                <div className={
                    "flex flex-col items-center " +
                    (isCarouselHidden ? "w-11/12" : "w-1/2")
                }
                >
                    <p className="text-5xl">{title}</p>

                    <div className="flex flex-col justify-between mt-12 p-10 min-w-96 w-3/12">

                        <div className="w-full">
                            <div className="mt-8">
                                {
                                    (() => {
                                        if (!loginIsFilled) {
                                            return <EnterLogin
                                                login={login}
                                                setLogin={setLogin}
                                                errors={errors}
                                                onSubmit={continueButton}
                                            />
                                        } else if (isLoginPage) {
                                            return <LoginForm
                                                login={login}
                                                errors={errors}
                                                setPassword={setPassword}
                                                backToEnterLogin={backToEnterLogin}
                                                onSubmit={continueButton}
                                            />
                                        } else {
                                            return <RegisterForm
                                                setMail={(mail: string ) => {
                                                    if (errors.mailIsOnError) {
                                                        setErrors(prevState => ({
                                                            ...prevState,
                                                            mailIsOnError: false,
                                                            mailErrorMessage: "",
                                                        }))
                                                    }
                                                    setMail(mail)
                                                }}
                                                setCountry={(country: string) => {
                                                    if (errors.countryIsOnError) {
                                                        setErrors(prevState => ({
                                                            ...prevState,
                                                            countryIsOnError: false,
                                                            countryErrorMessage: "",
                                                        }))
                                                    }
                                                    setCountry(country)
                                                }}
                                                country={country}
                                                setPassword={(password: string) => {
                                                    if (errors.passwordIsOnError) {
                                                        setErrors(prevState => ({
                                                            ...prevState,
                                                            passwordIsOnError: false,
                                                            passwordErrorMessage: "",
                                                        }))
                                                    }
                                                    setPassword(password)
                                                }}
                                                setTitle={setTitle}
                                                errors={errors}
                                                onSubmit={continueButton}
                                            />
                                        }
                                    })()
                                }
                            </div>
                        </div>

                        {/* action buttons and global error msg */}
                        <div className="flex flex-col justify-center items-center mt-10 gap-2 w-full">
                            {loginIsFilled && (
                                <UiButton
                                    className="w-full"
                                    type={ButtonType.transparentBg}
                                    onClick={backToEnterLogin}
                                    text={t("global.back-to-login")}
                                />
                            )}

                            <UiButton
                                className="w-full"
                                onClick={continueButton}
                                text={continueButtonText}
                                icon="arrow-left"
                                iconClassName="rotate-180 w-8"
                                iconOnRight={true}
                            />
                        </div>

                        <div className="mt-4 min-h-8">
                            {errors.globalErrorMessage !== "" && (
                                <p className="text-error text-xl text-center font-semibold">
                                    {errors.globalErrorMessage}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Carousel */}
                <div className={
                    "hidden xl:flex h-full transform transition-[width] duration-300  " +
                    (isCarouselHidden ? "w-1/12" : "w-1/2")
                }>
                    {/* button to hide the carousel */}
                    <div className="flex h-full items-end min-w-12 w-1/12">
                        <button
                            onClick={() => {
                                setisCarouselHidden(!isCarouselHidden)
                            }}
                        >
                            <img
                                className={"h-8 w-8 mb-8 transform " +
                                    "transition-transform duration-300 " +
                                    (isCarouselHidden ? "-rotate-90": "rotate-90")
                                }
                                src="/icons/arrow.svg"
                                alt={t("login.reduce-carousel")}
                            />
                        </button>
                    </div>

                    <div className="h-full bg-darkpurple-blue rounded-l-3xl w-11/12">
                        {!isCarouselHidden && <LoginCarousel />}
                    </div>
                </div>
            </div>
        </div>
    )
}
